<template>
	<div class="input-wrapper input-wrapper--select-weeks">
		<label for="po-select-delivery-dates">
			{{ t('shoppingcart.modal.createpo.content.selectWeeks.label') }}:
		</label>

		<button class="modal-wrapper__select-all" type="button" @click="toggleDeliveryDateSelection">
			<span v-text="getToggleSelectText"></span>
		</button>

		<multiselect id="po-select-delivery-dates"
		             :allow-empty="true"
		             :close-on-select="false"
		             :disabled="deliveryDateOptions.length < 1"
		             :model-value="selectedDeliveryDates"
		             :multiple="true"
		             :options="deliveryDateOptions"
		             :placeholder="t('shoppingcart.modal.createpo.content.selectWeeks.label')"
		             :searchable="false"
		             deselect-label=""
		             select-label=""
		             selected-label=""
		             track-by="id"
		             @update:model-value="updateSelectedDeliveryDates">

			<template v-slot:option="{ option }">
				{{ getNormalizedWeekIncludingLabel(option) }}

				<small v-if="option.message" style="float: right;">
					<svg class="icon-notice" height="16" style="margin-left: 6px;" width="16">
						<use xlink:href="/assets/images/build/svg-sprite.catalog.min.svg#notice"></use>
					</svg>
					{{ option.message }}
				</small>
			</template>
			<template v-slot:tag="{ option, remove }">
				<span :class="{ 'multiselect__tag--disabled': option.hasInvalidOrderLines }" class="multiselect__tag">
					<span>{{ getNormalizedWeekIncludingLabel(option) }}</span>
					<i class="multiselect__tag-icon"
					   tabindex="1"
					   @keypress.enter.prevent="remove(option)"
					   @mousedown.prevent="remove(option)"></i>
				</span>
			</template>
		</multiselect>

	</div>
</template>

<script lang="ts" setup>

	import Multiselect from 'vue-multiselect';
	import {useI18n} from 'vue-i18n';
	import {computed, onBeforeMount} from 'vue';
	import type {DeliveryDateOption} from '@/types/DeliveryDateOption';
	import {type DeliveryDate, isOrderLinesInvalid} from 'dummen-shared';
	import {useShoppingCartStore} from '@/store/shoppingcart.store.ts';

	const {t} = useI18n();
	const shoppingCartStore = useShoppingCartStore();

	const props = defineProps<{
		selectedDeliveryDates: DeliveryDateOption[];
		deliveryDateOptions: DeliveryDate[];
	}>();

	const emit = defineEmits(['on-selected-delivery-date-change']);

	const hasSelectedDeliveryDates = computed(() => {
		return props.selectedDeliveryDates && props.selectedDeliveryDates.length > 0;
	});

	const getMessage = (hasInvalidOrderLines: boolean) => {
		return hasInvalidOrderLines
			? t('shoppingcart.modal.createpo.content.error.unavailableWeek.text')
			: null;
	};

	const deliveryDateOptions = computed((): DeliveryDateOption[] => {
		const options: DeliveryDateOption[] = [];

		for (const deliveryDateOption of props.deliveryDateOptions) {
			const orderLinesForWeek = shoppingCartStore.getOrderLinesForWeek(deliveryDateOption.week, deliveryDateOption.year);
			const weekHasInvalidOrderLines = isOrderLinesInvalid(orderLinesForWeek);
			if (!orderLinesForWeek) {
				continue;
			}

			options.push({
				id: `${deliveryDateOption.year}${deliveryDateOption.week}`,
				year: deliveryDateOption.year,
				week: deliveryDateOption.week,
				hasInvalidOrderLines: weekHasInvalidOrderLines,
				message: getMessage(weekHasInvalidOrderLines)
			});
		}

		return options;
	});

	const hasUnavailableDeliveryDates = computed(() => {
		return props.selectedDeliveryDates.some((option: DeliveryDateOption) => option.hasInvalidOrderLines);
	});

	const getToggleSelectText = computed(() => {
		return hasSelectedDeliveryDates.value
			? t('shoppingcart.modal.createpo.content.removeAllWeeks.text')
			: t('shoppingcart.modal.createpo.content.selectAllWeeks.text');
	});

	const getNormalizedWeekIncludingLabel = (deliveryDate: DeliveryDateOption) => {
		return t('shoppingcart.generic.week.text') + ' ' + deliveryDate.week;
	};

	const updateSelectedDeliveryDates = (selectedDeliveryDates: DeliveryDateOption[]) => {
		emit('on-selected-delivery-date-change', selectedDeliveryDates);
	};

	const selectAllDeliveryDateOptions = () => {
		const selectedDeliveryDates = deliveryDateOptions.value
		updateSelectedDeliveryDates(selectedDeliveryDates);
	};

	const deselectAllDeliveryDateOptions = () => updateSelectedDeliveryDates([]);

	const toggleDeliveryDateSelection = () => {
		// Deselect all weeks if minimal one is currently selected.
		if (hasSelectedDeliveryDates.value) {
			deselectAllDeliveryDateOptions()
		} else {
			selectAllDeliveryDateOptions();
		}
	};

	onBeforeMount(() => {
		selectAllDeliveryDateOptions();
	});

</script>

<style lang="scss" scoped>
	@use "sass:math";

	.multiselect__tag--disabled {
		background: rgb(255, 80, 0);
	}
</style>
